import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Plus, MagnifyingGlass, ClipboardText, Trash, PaperPlaneTilt, CheckCircle,
    CurrencyInr, X, DownloadSimple, Paperclip, ArrowsClockwise,
} from "@phosphor-icons/react";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";

const STATUS = {
    draft:               { label: "Draft",            cls: "bg-slate-100 text-slate-700 border-slate-300" },
    sent:                { label: "Sent",             cls: "bg-amber-50 text-amber-800 border-amber-300" },
    partially_received:  { label: "Partial",          cls: "bg-amber-100 text-amber-900 border-amber-400" },
    received:            { label: "Received",         cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
    paid:                { label: "Paid",             cls: "bg-emerald-100 text-emerald-900 border-emerald-400" },
    cancelled:           { label: "Cancelled",        cls: "bg-rose-50 text-rose-700 border-rose-300" },
};

const formatINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDT = (s) => s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";


export function PurchaseOrdersTab({ onChange }) {
    const [pos, setPos] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [showCreate, setShowCreate] = useState(false);
    const [openPO, setOpenPO] = useState(null);

    const load = () => api.get("/admin/inventory/purchase-orders").then((r) => setPos(r.data || []));
    useEffect(() => {
        load();
        api.get("/admin/inventory/vendors").then((r) => setVendors(r.data || []));
        api.get("/admin/inventory/materials").then((r) => setMaterials(r.data || []));
    }, []);

    const filtered = useMemo(() => {
        let list = pos;
        if (status !== "all") list = list.filter((p) => p.status === status);
        if (q.trim()) {
            const ql = q.toLowerCase();
            list = list.filter((p) =>
                (p.code || "").toLowerCase().includes(ql) ||
                (p.vendor_name || "").toLowerCase().includes(ql)
            );
        }
        return list;
    }, [pos, status, q]);

    const statusCounts = useMemo(() => {
        const c = { all: pos.length, draft: 0, sent: 0, partially_received: 0, received: 0, paid: 0, cancelled: 0 };
        pos.forEach((p) => { if (c[p.status] !== undefined) c[p.status] += 1; });
        return c;
    }, [pos]);

    return (
        <div className="space-y-4" data-testid="purchase-orders-tab">
            <FilterSearchBar
                query={q}
                setQuery={setQ}
                placeholder="Search PO code or vendor…"
                searchTestid="po-search"
                testid="po-search-bar"
                rightSlot={(
                    <Button size="sm" className="rounded-full" onClick={() => setShowCreate(true)} data-testid="po-add">
                        <Plus size={14} weight="bold" className="mr-1.5" /> Create PO
                    </Button>
                )}
            />

            <FilterChipRow
                testid="po-chip-row"
                chipTestidPrefix="po-filter"
                value={status}
                onChange={setStatus}
                chips={[
                    { id: "all",                label: "All",       count: statusCounts.all,                tone: "primary" },
                    { id: "draft",              label: "Draft",     count: statusCounts.draft,              tone: "slate" },
                    { id: "sent",               label: "Sent",      count: statusCounts.sent,               tone: "amber" },
                    { id: "partially_received", label: "Partial",   count: statusCounts.partially_received, tone: "amber" },
                    { id: "received",           label: "Received",  count: statusCounts.received,           tone: "emerald" },
                    { id: "paid",               label: "Paid",      count: statusCounts.paid,               tone: "emerald" },
                    { id: "cancelled",          label: "Cancelled", count: statusCounts.cancelled,          tone: "rose" },
                ]}
            />

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                    <div className="col-span-2">Code</div>
                    <div className="col-span-3">Vendor</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2 text-right">Total</div>
                    <div className="col-span-2">Expected</div>
                    <div className="col-span-1 text-right">Lines</div>
                </div>
                {filtered.length === 0 ? (
                    <div className="py-10 text-center text-muted-foreground text-sm" data-testid="po-empty">
                        {pos.length === 0 ? "No purchase orders yet — create one to start procuring." : "No POs match your filters."}
                    </div>
                ) : filtered.map((p) => {
                    const t = STATUS[p.status] || STATUS.draft;
                    return (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => setOpenPO(p)}
                            className="w-full grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 items-center hover:bg-secondary/30 transition text-left"
                            data-testid={`po-row-${p.id}`}
                        >
                            <div className="col-span-2 font-mono text-xs font-bold">{p.code}</div>
                            <div className="col-span-3 text-sm truncate">{p.vendor_name || "—"}</div>
                            <div className="col-span-2">
                                <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded border ${t.cls}`}>{t.label}</span>
                            </div>
                            <div className="col-span-2 text-right font-bold tabular-nums text-sm">{formatINR(p.total)}</div>
                            <div className="col-span-2 text-xs text-muted-foreground">{fmtDT(p.expected_at)}</div>
                            <div className="col-span-1 text-right text-xs text-muted-foreground tabular-nums">{(p.lines || []).length}</div>
                        </button>
                    );
                })}
            </div>

            <CreatePODialog
                open={showCreate}
                onClose={() => setShowCreate(false)}
                vendors={vendors}
                materials={materials}
                onCreated={(po) => { setShowCreate(false); load(); onChange?.(); setOpenPO(po); }}
            />

            {openPO && (
                <PODetailDialog
                    po={openPO}
                    onClose={() => setOpenPO(null)}
                    onChanged={() => { load(); onChange?.(); }}
                />
            )}
        </div>
    );
}


function CreatePODialog({ open, onClose, vendors, materials, onCreated }) {
    const [vendorId, setVendorId] = useState("");
    const [expected, setExpected] = useState("");
    const [taxPct, setTaxPct] = useState(18);
    const [notes, setNotes] = useState("");
    const [lines, setLines] = useState([]);

    useEffect(() => {
        if (open) {
            setVendorId(vendors[0]?.id || "");
            setExpected(""); setTaxPct(18); setNotes(""); setLines([]);
        }
    }, [open, vendors]);

    const addLine = () => setLines([...lines, { material_id: materials[0]?.id || "", quantity: 1, unit_cost: 0 }]);
    const updateLine = (idx, patch) => setLines(lines.map((l, i) => i === idx ? { ...l, ...patch } : l));
    const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

    const totals = useMemo(() => {
        const sub = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0), 0);
        const tax = sub * ((Number(taxPct) || 0) / 100);
        return { subtotal: sub, tax_amount: tax, total: sub + tax };
    }, [lines, taxPct]);

    const submit = async () => {
        if (!vendorId) { toast.error("Select a vendor"); return; }
        if (lines.length === 0) { toast.error("Add at least one line item"); return; }
        for (const l of lines) {
            if (!l.material_id) { toast.error("Each line needs a material"); return; }
            if (!l.quantity || Number(l.quantity) <= 0) { toast.error("Quantity must be > 0"); return; }
        }
        try {
            const r = await api.post("/admin/inventory/purchase-orders", {
                vendor_id: vendorId,
                expected_at: expected || null,
                tax_pct: Number(taxPct) || 0,
                notes,
                lines: lines.map((l) => ({ material_id: l.material_id, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost) || 0, notes: l.notes || "" })),
            });
            toast.success(`PO ${r.data.code} created as draft`);
            onCreated(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-create-dialog">
                <DialogHeader>
                    <DialogTitle>Create Purchase Order</DialogTitle>
                    <DialogDescription>Saved as a draft. You can edit, send, and receive after creation.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                        <Label>Vendor</Label>
                        <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="po-vendor">
                            <option value="">Select vendor…</option>
                            {vendors.filter((v) => v.is_active !== false).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label>Expected delivery</Label>
                        <Input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} data-testid="po-expected" />
                    </div>
                </div>

                <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                        <Label>Line items</Label>
                        <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="po-add-line"><Plus size={12} className="mr-1" /> Add line</Button>
                    </div>
                    {lines.length === 0 ? (
                        <div className="text-xs text-muted-foreground py-6 text-center bg-secondary/30 rounded-xl">No lines. Add at least one material.</div>
                    ) : (
                        <div className="space-y-2">
                            {lines.map((l, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`po-create-line-${idx}`}>
                                    <div className="col-span-5">
                                        <Label className="text-[10px]">Material</Label>
                                        <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-xs" value={l.material_id} onChange={(e) => updateLine(idx, { material_id: e.target.value })}>
                                            <option value="">—</option>
                                            {materials.filter((m) => m.is_active !== false).map((m) => <option key={m.id} value={m.id}>{m.sku} — {m.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="text-[10px]">Qty</Label>
                                        <Input className="h-9" type="number" value={l.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} />
                                    </div>
                                    <div className="col-span-3">
                                        <Label className="text-[10px]">Unit cost ₹</Label>
                                        <Input className="h-9" type="number" value={l.unit_cost} onChange={(e) => updateLine(idx, { unit_cost: e.target.value })} />
                                    </div>
                                    <div className="col-span-1 text-right text-[11px] font-bold tabular-nums">
                                        {formatINR((Number(l.quantity) || 0) * (Number(l.unit_cost) || 0))}
                                    </div>
                                    <div className="col-span-1 text-right">
                                        <button type="button" onClick={() => removeLine(idx)} className="p-1 text-rose-600 hover:bg-rose-50 rounded">
                                            <Trash size={12} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-3 gap-3 mt-3">
                    <div>
                        <Label>Tax %</Label>
                        <Input type="number" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} data-testid="po-tax" />
                    </div>
                    <div className="col-span-2 self-end text-right space-y-0.5 text-sm">
                        <div className="text-muted-foreground">Subtotal <span className="font-bold tabular-nums ml-2">{formatINR(totals.subtotal)}</span></div>
                        <div className="text-muted-foreground">Tax <span className="font-bold tabular-nums ml-2">{formatINR(totals.tax_amount)}</span></div>
                        <div className="font-display text-xl font-bold tabular-nums">{formatINR(totals.total)}</div>
                    </div>
                </div>
                <div>
                    <Label>Notes (optional)</Label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="po-notes" />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} data-testid="po-create-submit">Create draft</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function PODetailDialog({ po: initialPO, onClose, onChanged }) {
    const [po, setPo] = useState(initialPO);
    const [busy, setBusy] = useState(false);
    const [showReceive, setShowReceive] = useState(false);
    const [uploadingInv, setUploadingInv] = useState(false);

    /* Always refetch on mount to pick up latest lines/quantity_received */
    useEffect(() => {
        api.get(`/admin/inventory/purchase-orders/${initialPO.id}`).then((r) => setPo(r.data));
    }, [initialPO.id]);

    const refresh = async () => {
        const r = await api.get(`/admin/inventory/purchase-orders/${initialPO.id}`);
        setPo(r.data);
        onChanged?.();
    };

    const transition = async (path, label) => {
        if (!await confirmDialog({ title: `${label} this PO?`, confirmText: "Confirm", tone: "default" })) return;
        setBusy(true);
        try {
            await api.post(`/admin/inventory/purchase-orders/${initialPO.id}/${path}`);
            toast.success(`${label} done`);
            await refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    const uploadInvoice = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setUploadingInv(true);
        const fd = new FormData(); fd.append("file", f);
        try {
            await api.post(`/admin/inventory/purchase-orders/${initialPO.id}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Vendor invoice uploaded");
            await refresh();
        } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
        setUploadingInv(false);
    };

    if (!po) return null;
    const t = STATUS[po.status] || STATUS.draft;
    const canSend = po.status === "draft";
    const canReceive = po.status === "sent" || po.status === "partially_received";
    const canMarkPaid = po.status === "received" || po.status === "partially_received";
    const canCancel = ["draft", "sent"].includes(po.status);

    return (
        <Dialog open={!!po} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        <span>{po.code}</span>
                        <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded border ${t.cls}`}>{t.label}</span>
                    </DialogTitle>
                    <DialogDescription>{po.vendor_name} · Expected {fmtDT(po.expected_at)}</DialogDescription>
                </DialogHeader>

                {/* Status timeline */}
                <div className="grid grid-cols-4 gap-2 text-[10px] uppercase tracking-wide" data-testid="po-timeline">
                    <TimelineDot label="Sent" date={po.sent_at} active />
                    <TimelineDot label="Received" date={po.received_at} active={!!po.received_at || po.status === "partially_received"} />
                    <TimelineDot label="Paid" date={po.paid_at} active={!!po.paid_at} />
                    <TimelineDot label="Cancelled" date={po.cancelled_at} active={!!po.cancelled_at} tone="rose" />
                </div>

                {/* Lines */}
                <div className="rounded-xl border border-border bg-secondary/20 overflow-hidden mt-3">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground border-b border-border">
                        <div className="col-span-5">Material</div>
                        <div className="col-span-2 text-right">Ordered</div>
                        <div className="col-span-2 text-right">Received</div>
                        <div className="col-span-1 text-right">Cost</div>
                        <div className="col-span-2 text-right">Line</div>
                    </div>
                    {(po.lines || []).map((li) => (
                        <div key={li.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-0 text-sm" data-testid={`po-line-${li.id}`}>
                            <div className="col-span-5 min-w-0">
                                <div className="font-mono text-[10px] text-muted-foreground">{li.material_sku}</div>
                                <div className="truncate">{li.material_name}</div>
                            </div>
                            <div className="col-span-2 text-right tabular-nums">{li.quantity} <span className="text-muted-foreground text-xs">{li.unit}</span></div>
                            <div className="col-span-2 text-right tabular-nums">
                                <span className={li.quantity_received >= li.quantity ? "text-emerald-700 font-bold" : "text-amber-700 font-bold"}>{li.quantity_received || 0}</span>
                            </div>
                            <div className="col-span-1 text-right tabular-nums text-xs text-muted-foreground">{formatINR(li.unit_cost)}</div>
                            <div className="col-span-2 text-right tabular-nums font-semibold">{formatINR(li.line_total)}</div>
                        </div>
                    ))}
                    <div className="px-3 py-2 border-t border-border bg-card text-right text-sm space-y-0.5">
                        <div className="text-muted-foreground">Subtotal <span className="font-bold ml-2 tabular-nums">{formatINR(po.subtotal)}</span></div>
                        <div className="text-muted-foreground">Tax ({po.tax_pct}%) <span className="font-bold ml-2 tabular-nums">{formatINR(po.tax_amount)}</span></div>
                        <div className="font-display text-lg font-bold tabular-nums">{formatINR(po.total)}</div>
                    </div>
                </div>

                {po.notes && <div className="text-xs text-muted-foreground bg-secondary/30 p-3 rounded-xl mt-2">{po.notes}</div>}

                {/* Invoice attachment */}
                <div className="flex items-center justify-between gap-2 mt-3 p-3 rounded-xl border border-dashed border-border">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Paperclip size={13} />
                        {po.invoice_storage_path ? <span>Vendor invoice attached</span> : <span>No vendor invoice attached yet</span>}
                    </div>
                    <div className="flex items-center gap-2">
                        {po.invoice_storage_path && (
                            <a href={`/api/admin/inventory/purchase-orders/${po.id}/invoice`} target="_blank" rel="noreferrer" className="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1" data-testid="po-invoice-download">
                                <DownloadSimple size={12} /> Download
                            </a>
                        )}
                        <label className="text-xs font-semibold text-primary hover:underline cursor-pointer inline-flex items-center gap-1" data-testid="po-invoice-upload-label">
                            <input type="file" className="hidden" accept="application/pdf,image/jpeg,image/png" onChange={uploadInvoice} data-testid="po-invoice-upload-input" />
                            {uploadingInv ? "Uploading…" : "Upload PDF"}
                        </label>
                    </div>
                </div>

                <DialogFooter className="flex-wrap gap-2">
                    {canCancel && (
                        <Button variant="outline" disabled={busy} onClick={() => transition("cancel", "Cancel")} className="text-rose-700 hover:bg-rose-50" data-testid="po-cancel">
                            <X size={12} className="mr-1" /> Cancel PO
                        </Button>
                    )}
                    {canSend && (
                        <Button disabled={busy} onClick={() => transition("send", "Send")} data-testid="po-send">
                            <PaperPlaneTilt size={12} className="mr-1" /> Send to vendor
                        </Button>
                    )}
                    {canReceive && (
                        <Button disabled={busy} onClick={() => setShowReceive(true)} data-testid="po-receive">
                            <CheckCircle size={12} className="mr-1" /> Receive goods
                        </Button>
                    )}
                    {canMarkPaid && (
                        <Button disabled={busy} onClick={() => transition("mark-paid", "Mark paid")} data-testid="po-mark-paid">
                            <CurrencyInr size={12} className="mr-1" /> Mark paid
                        </Button>
                    )}
                    <Button variant="ghost" onClick={onClose}>Close</Button>
                </DialogFooter>

                {showReceive && (
                    <ReceiveDialog
                        po={po}
                        onClose={() => setShowReceive(false)}
                        onReceived={async () => { setShowReceive(false); await refresh(); }}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}


function TimelineDot({ label, date, active, tone }) {
    const dotCls = active
        ? (tone === "rose" ? "bg-rose-500" : "bg-emerald-500")
        : "bg-secondary";
    return (
        <div className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${dotCls}`} />
            <div className="min-w-0">
                <div className="font-bold text-foreground">{label}</div>
                <div className="normal-case text-[10px] text-muted-foreground truncate">{date ? fmtDT(date) : "—"}</div>
            </div>
        </div>
    );
}


function ReceiveDialog({ po, onClose, onReceived }) {
    const [items, setItems] = useState(() =>
        (po.lines || []).map((l) => ({
            line_id: l.id,
            remaining: (Number(l.quantity) || 0) - (Number(l.quantity_received) || 0),
            qty: "",
        }))
    );
    const [note, setNote] = useState("");
    const submit = async () => {
        const lines = items
            .map((it) => ({ line_id: it.line_id, quantity_received: Number(it.qty) }))
            .filter((it) => Number.isFinite(it.quantity_received) && it.quantity_received > 0);
        if (lines.length === 0) { toast.error("Enter a received quantity for at least one line"); return; }
        try {
            await api.post(`/admin/inventory/purchase-orders/${po.id}/receive`, { lines, note });
            toast.success("Receipt recorded — stock updated");
            onReceived?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };
    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="po-receive-dialog">
                <DialogHeader>
                    <DialogTitle>Receive goods — {po.code}</DialogTitle>
                    <DialogDescription>Enter received quantities. Each one updates stock & writes a movement log.</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                    {po.lines?.map((li, idx) => {
                        const remaining = (Number(li.quantity) || 0) - (Number(li.quantity_received) || 0);
                        return (
                            <div key={li.id} className="flex items-center gap-2" data-testid={`po-receive-line-${li.id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{li.material_name}</div>
                                    <div className="text-[11px] text-muted-foreground">Remaining: {remaining} {li.unit}</div>
                                </div>
                                <Input
                                    type="number"
                                    className="w-24 h-9"
                                    placeholder="0"
                                    value={items[idx]?.qty || ""}
                                    onChange={(e) => setItems(items.map((it, i) => i === idx ? { ...it, qty: e.target.value } : it))}
                                    data-testid={`po-receive-qty-${li.id}`}
                                />
                            </div>
                        );
                    })}
                </div>
                <div>
                    <Label>Note (optional)</Label>
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="po-receive-note" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} data-testid="po-receive-submit">Receive</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
